import React, { useState, useEffect } from 'react';
import {
  Toolbar, IconButton, TextField, Box, Menu, MenuItem, Avatar,
  Typography, Tooltip, Button, useMediaQuery
} from '@mui/material';

import {
  Logout as LogoutIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CreditCard as CreditCardIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReplayIcon from '@mui/icons-material/Replay';
import { useTheme } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

import Logo1 from '../Logo/Dark/Icon - Copy/1Asset 15@72x-8.png';
import Logo from '../Logo/Dark/Full - Copy/1Asset 17@72x-8.png';

const API_URL = process.env.REACT_APP_API_URL;

function Header1({ userId, onLogout, page_name, onSearch }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [userData, setUserData] = useState({ name: '', surname: '', image: '', role: '' });
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSearchActive, setIsSearchActive] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const allowedPages = ['posted', 'category', 'recent-activity', 'posts', 'bookings', 'request_topic', 'request_user', 'recommended_organizations_list'];

  useEffect(() => {
    if (!userId) return;

    axios.get(`${API_URL}/get_user_data/?user_id=${userId}`)
      .then((res) => setUserData(res.data))
      .catch(console.error);

    axios.post(`${API_URL}/api/check-verification/`, { user_id: userId })
      .then((res) => setIsVerified(res.data.status === 'approved'))
      .catch(() => setIsVerified(false))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      fetch(`${API_URL}/get_search_results/?page_name=${page_name}&search_input=${searchQuery}&user_id=${userId}`)
        .then(res => res.json())
        .then(data => {
          onSearch(data);
          setSearchQuery('');
          setIsSearchActive(false);
        })
        .catch(console.error);
    }
  };

  const handleClear = () => setSearchQuery('');
  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  return (
    <>
      {/* Header Container using Box */}
      <Box
        sx={{
          width: '100%',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1100,
          bgcolor: '#000000',
          color: 'white',
          px: 2,
          // py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // borderBottom: '1px solid #333',
        }}
      >
        {/* Logo Section */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/home" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src={isMobile ? Logo1 : Logo} alt="Logo" style={{ width: isMobile ? '40px' : '120px' }} />
          </Link>
        </Box>

        {/* Search Bar - only on allowed pages and non-mobile */}
        {allowedPages.includes(page_name) && !isMobile && (
          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: 600,
              bgcolor: '#000000',
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              mx: 2,
              flexGrow: 1,
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {searchQuery && (
                      <Tooltip title="Clear">
                        <IconButton onClick={handleClear} size="small" sx={{ color: 'white' }}>
                          <ClearIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Search">
                      <IconButton type="submit" size="small" sx={{ color: 'white' }}>
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ),
                sx: {
                  input: { color: 'white' },
                  backgroundColor: '#1c1c1c',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                },
              }}
            />
          </Box>
        )}

        {/* Actions Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMobile && (
            <Tooltip title="Search">
              <IconButton onClick={() => setIsSearchActive(true)} sx={{ color: 'white' }}>
                <SearchIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Platform" placement="bottom">
            <IconButton component={Link} to={`/platform/${userId}`} color="inherit">
              <Typography
              component="h1"
                sx={{
                  backgroundImage: "linear-gradient(45deg, #00bfff, #ff00ff)",
                  backgroundClip: "text",
                  color: "transparent",
                  letterSpacing: 4,
                  
                  fontSize: { xs: '0.7rem', sm: '1rem' },
                }}
              >
                Vimitra
              </Typography>
              <Box
                sx={{
                  ml: 1,
                  px: 0.5,
                  py: 0.1,
                  bgcolor: '#1E90FF',
                  borderRadius: '4px',
                  color: 'white',
                  fontSize: { xs: '0.55rem', sm: '0.7rem' },
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                }}
              >
                Beta
              </Box>
            </IconButton>
          </Tooltip>

          <Tooltip title="Profile">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: 'white' }}>
              <Avatar sx={{ bgcolor: '#444' }}>
                {(userData.name?.[0] || '') + (userData.surname?.[0] || '')}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Spacer for fixed header */}
      <Box sx={{ height: '64px' }} />

      {/* Mobile Fullscreen Search */}
      {isMobile && isSearchActive && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.95)',
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: 2,
          }}
        >
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit(e)}
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleSearchSubmit} color="primary">
                  <SearchIcon />
                </IconButton>
              ),
              sx: {
                backgroundColor: 'white',
                borderRadius: 2,
              },
            }}
          />
          <Button onClick={() => setIsSearchActive(false)} sx={{ mt: 2, color: 'white' }}>
            Cancel
          </Button>
        </Box>
      )}

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            bgcolor: 'black',
            color: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          },
        }}
      >
        <MenuItem component={Link} to={`/UserProfile/${userId}`} onClick={() => setAnchorEl(null)}>
          <PersonIcon fontSize="small" sx={{ mr: 1 }} /> User Portal
        </MenuItem>
        {userData.role === 'admin' && (
          <MenuItem component={Link} to="/admin-panel" onClick={() => setAnchorEl(null)}>
            <PersonIcon fontSize="small" sx={{ mr: 1 }} /> Admin Panel
          </MenuItem>
        )}
        <MenuItem component={Link} to="/Documentation" onClick={() => setAnchorEl(null)}>
          <DescriptionIcon fontSize="small" sx={{ mr: 1 }} /> Documentation
        </MenuItem>
        <MenuItem component={Link} to="/transactions" onClick={() => setAnchorEl(null)}>
          <ReceiptIcon fontSize="small" sx={{ mr: 1 }} /> Transactions
        </MenuItem>
        {isVerified && (
          <MenuItem component={Link} to="/teacher-sessions" onClick={() => setAnchorEl(null)}>
            <PaymentsIcon fontSize="small" sx={{ mr: 1 }} /> Payments
          </MenuItem>
        )}
        <MenuItem component={Link} to="/user-refunds" onClick={() => setAnchorEl(null)}>
            <ReplayIcon fontSize="small" sx={{ mr: 1 }} /> Refunds
          </MenuItem>
         
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            handleLogout();
          }}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
        </MenuItem>
      </Menu>
    </>
  );
}

export default Header1;
